import '../../design/analyticStyle.css'
import { Bar, Pie } from "react-chartjs-2"
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    ArcElement,
    Tooltip,
    Legend,
    Filler
} from "chart.js"
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API_URL } from "../../api/API_SERVERS";
import { HiOutlineUsers, HiOutlineCash, HiOutlineTrendingUp, HiOutlinePresentationChartBar } from "react-icons/hi";

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    ArcElement,
    Tooltip,
    Legend,
    Filler
)

export default function Analytics() {
    const [monthlyCount, setMonthlyCount] = useState(0);
    const [yearlyCount, setYearlyCount] = useState(0);
    const [availableYears, setAvailableYears] = useState([]);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [loading, setLoading] = useState(false);

    // Separated Data Arrays
    const [paypalMonthlyIncome, setPaypalMonthlyIncome] = useState(Array(12).fill(0));
    const [adsMonthlyIncome, setAdsMonthlyIncome] = useState(Array(12).fill(0));
    const [paypalDailyIncome, setPaypalDailyIncome] = useState([]);
    const [adsDailyIncome, setAdsDailyIncome] = useState([]);

    // Cumulative Summaries
    const [allTimePaypal, setAllTimePaypal] = useState(0);
    const [allTimeAds, setAllTimeAds] = useState(0);

    const COLORS = {
        paypal: '#0284c7',  // Corporate Sky Blue
        ads: '#10b981',     // Accounting Emerald Green
        primary: '#6366f1',
        warning: '#f59e0b',
        border: '#ffffff'
    };

    const processFinancialData = useCallback((payments, adsList, year) => {
        const paypalMonth = Array(12).fill(0);
        const adsMonth = Array(12).fill(0);

        let totalPaypalSum = 0;
        let totalAdsSum = 0;

        // 1. Map Paypals
        payments.forEach(payment => {
            const amt = Number(payment.amount) || 0;
            totalPaypalSum += amt;
            const dateStr = payment.created_at || payment.date;
            if (dateStr) {
                const date = new Date(dateStr);
                if (date.getFullYear() === year) {
                    paypalMonth[date.getMonth()] += amt;
                }
            }
        });

        // 2. Map Ads with 30-day shift back to initial cashflow day
        adsList.forEach(ad => {
            const price = Number(ad.price) || 0;
            totalAdsSum += price;
            const dateStr = ad.expires_at || ad.created_at || ad.date;
            if (dateStr) {
                const date = new Date(dateStr);
                if (ad.expires_at) {
                    date.setDate(date.getDate() - 30);
                }
                if (date.getFullYear() === year) {
                    adsMonth[date.getMonth()] += price;
                }
            }
        });

        setPaypalMonthlyIncome(paypalMonth);
        setAdsMonthlyIncome(adsMonth);
        setAllTimePaypal(totalPaypalSum);
        setAllTimeAds(totalAdsSum);

        // 3. Build Daily Vector Tracks
        const today = new Date();
        const curYear = today.getFullYear();
        const curMonth = today.getMonth();
        const daysInMonth = new Date(curYear, curMonth + 1, 0).getDate();

        const pDaily = Array.from({ length: daysInMonth }, (_, i) => ({ day: i + 1, amount: 0 }));
        const aDaily = Array.from({ length: daysInMonth }, (_, i) => ({ day: i + 1, amount: 0 }));

        payments.forEach(p => {
            const dateStr = p.created_at || p.date;
            if (dateStr) {
                const d = new Date(dateStr);
                if (d.getFullYear() === curYear && d.getMonth() === curMonth) {
                    pDaily[d.getDate() - 1].amount += (Number(p.amount) || 0);
                }
            }
        });

        adsList.forEach(ad => {
            const price = Number(ad.price) || 0;
            const dateStr = ad.expires_at || ad.created_at || ad.date;
            if (dateStr) {
                const d = new Date(dateStr);
                if (ad.expires_at) {
                    d.setDate(d.getDate() - 30);
                }
                if (d.getFullYear() === curYear && d.getMonth() === curMonth) {
                    aDaily[d.getDate() - 1].amount += price;
                }
            }
        });

        setPaypalDailyIncome(pDaily);
        setAdsDailyIncome(aDaily);
    }, []);

    const getAnalytic = useCallback(async () => {
        setLoading(true);
        try {
            const tokenHeader = { headers: { Authorization: 'Bearer ' + localStorage.getItem('AdminToken') } };
            const [paymentsRes, adsRes] = await Promise.all([
                axios.get(`${API_URL}/api/admin/payments-history`, tokenHeader),
                axios.get(`${API_URL}/api/admin/ads`, tokenHeader)
            ]);

            const history = paymentsRes.data.payments_history || [];
            const rawAds = adsRes.data?.ads || adsRes.data || {};

            const flatAds = [
                ...(rawAds.big || []),
                ...(rawAds.carousel || []),
                ...(rawAds.medium || []),
                ...(rawAds.small || [])
            ];

            setMonthlyCount(history.filter(item => item.payment_type === 'monthly').length);
            setYearlyCount(history.filter(item => item.payment_type === 'yearly').length);

            const yearsSet = new Set([
                ...history.map(item => new Date(item.created_at || item.date).getFullYear()),
                ...flatAds.map(item => {
                    const d = item.expires_at || item.created_at;
                    return new Date(d).getFullYear();
                })
            ]);
            const sortedYears = Array.from(yearsSet).sort((a, b) => b - a);
            setAvailableYears(sortedYears.length ? sortedYears : [new Date().getFullYear()]);

            processFinancialData(history, flatAds, selectedYear);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [selectedYear, processFinancialData]);

    useEffect(() => {
        getAnalytic();
    }, [getAnalytic]);

    const annualPaypalSum = paypalMonthlyIncome.reduce((a, b) => a + b, 0);
    const annualAdsSum = adsMonthlyIncome.reduce((a, b) => a + b, 0);
    const currentMonthPaypal = paypalDailyIncome.reduce((a, b) => a + b.amount, 0);
    const currentMonthAds = adsDailyIncome.reduce((a, b) => a + b.amount, 0);

    // Chart Options factory generating custom independent configurations
    const getChartConfig = (label, data, color) => ({
        labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
        datasets: [{ label, data, backgroundColor: color, borderRadius: 6 }]
    });

    const getDailyConfig = (label, dataVector, color) => ({
        labels: dataVector.map(item => item.day),
        datasets: [{ label, data: dataVector.map(item => item.amount), backgroundColor: color, borderRadius: 4 }]
    });

    const chartOptions = (symbol) => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: { y: { beginAtZero: true, ticks: { callback: (val) => symbol + val } } }
    });

    const subscriptionChart = {
        labels: ["Monthly Plan", "Yearly Plan"],
        datasets: [{
            data: [monthlyCount, yearlyCount],
            backgroundColor: [COLORS.primary, COLORS.warning],
            borderColor: COLORS.border,
            borderWidth: 4
        }]
    };

    return (
        <div className="analytics-container">
            {loading && <div className="loader-mini">Refreshing Ledgers...</div>}

            <header className="analytics-header">
                <h2>Financial Overview Dashboard</h2>
                <div className="year-selector-wrapper">
                    <label><HiOutlineCash /> Statement Period:</label>
                    <select value={selectedYear} onChange={(e) => setSelectedYear(Number(e.target.value))} className="year-selector">
                        {availableYears.map(year => (
                            <option key={year} value={year}>{year}</option>
                        ))}
                    </select>
                </div>
            </header>

            {/* Top Stat Row */}
            <div className="fin-scorecard-row">
                <div className="fin-card text-sky">
                    <span className="fin-title">All-Time PayPal Income</span>
                    <span className="fin-value">₾{allTimePaypal.toFixed(2)}</span>
                </div>
                <div className="fin-card text-emerald">
                    <span className="fin-title">All-Time Banner Ads Income</span>
                    <span className="fin-value">₾{allTimeAds.toFixed(2)}</span>
                </div>
                <div className="fin-card text-indigo">
                    <span className="fin-title"><HiOutlineTrendingUp /> Total Combined Gross</span>
                    <span className="fin-value">₾{(allTimePaypal + allTimeAds).toFixed(2)}</span>
                </div>
            </div>

            {/* SECTION 1: PAYPAL INCOME CHANNELS */}
            <div className="section-divider-title">
                <HiOutlinePresentationChartBar /> Income from PayPal
            </div>
            <div className="analytics-grid">
                <div className="analytics-card">
                    <h3>Annual PayPal Income ({selectedYear})</h3>
                    <div className="chart-container">
                        <Bar data={getChartConfig('PayPal', paypalMonthlyIncome, COLORS.paypal)} options={chartOptions('₾')} />
                    </div>
                    <div className="income-total-display">
                        <span className="label">Yearly PayPal Net</span>
                        <span className="amount font-sky">₾{annualPaypalSum.toFixed(2)}</span>
                    </div>
                </div>

                <div className="analytics-card">
                    <h3>Current Month Daily PayPal Income</h3>
                    <div className="chart-container">
                        <Bar data={getDailyConfig('Daily PayPal', paypalDailyIncome, COLORS.paypal)} options={chartOptions('₾')} />
                    </div>
                    <div className="income-total-display">
                        <span className="label">Monthly PayPal Net</span>
                        <span className="amount font-sky">₾{currentMonthPaypal.toFixed(2)}</span>
                    </div>
                </div>
            </div>

            {/* FUTURE: BANK OF GEORGIA INCOME CHANNELS GOES HERE */}
            {/* You can just drop an identical structure right here using a orange/orange-red theme color when you add the BOG api! */}

            {/* SECTION 2: ADVERTISEMENT MARKETING INCOME SECTION */}
            <div className="section-divider-title margin-top-large">
                <HiOutlinePresentationChartBar /> Advertisement Marketing Channels
            </div>
            <div className="analytics-grid">
                <div className="analytics-card">
                    <h3>Annual Banner Ad Revenues ({selectedYear})</h3>
                    <div className="chart-container">
                        <Bar data={getChartConfig('Ads', adsMonthlyIncome, COLORS.ads)} options={chartOptions('₾')} />
                    </div>
                    <div className="income-total-display">
                        <span className="label">Yearly Advertising Net</span>
                        <span className="amount font-emerald">₾{annualAdsSum.toFixed(2)}</span>
                    </div>
                </div>

                <div className="analytics-card">
                    <h3>Current Month Daily Ad Revenues</h3>
                    <div className="chart-container">
                        <Bar data={getDailyConfig('Daily Ads', adsDailyIncome, COLORS.ads)} options={chartOptions('₾')} />
                    </div>
                    <div className="income-total-display">
                        <span className="label">Monthly Advertising Net</span>
                        <span className="amount font-emerald">₾{currentMonthAds.toFixed(2)}</span>
                    </div>
                </div>
            </div>

            {/* Client Breakdown Section */}
            <div className="analytics-grid margin-top-large">
                <div className="analytics-card subscriptions-card">
                    <div className="pie-chart-container">
                        <Pie data={subscriptionChart} options={{ responsive: true, maintainAspectRatio: false }} />
                    </div>
                    <div className="subscription-details">
                        <h3>Client Accounts Summary</h3>
                        <div className="stat-row">
                            <span className="stat-label">Monthly Term Enrolled</span>
                            <span className="stat-value">{monthlyCount}</span>
                        </div>
                        <div className="stat-row">
                            <span className="stat-label">Yearly Term Enrolled</span>
                            <span className="stat-value">{yearlyCount}</span>
                        </div>
                        <div className="stat-row total-users">
                            <span className="stat-label"><HiOutlineUsers /> Cumulative Users Enrolled</span>
                            <span className="stat-value">{monthlyCount + yearlyCount}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}